import {
  HeartOutlined,
  HeartFilled,
  ArrowRightOutlined,
  HistoryOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Flex, Button, Table, TableProps, message, Tooltip } from "antd";
import { StudentRequirementsTutorsShortlistedTableProps } from "../types";
import { renderBoolean, renderDistanceLinkGen } from "../../table-utils";
import { useEffect, useState } from "react";
import axios from "../../../axios";
import { SHORTLISTED_TUTOR_PROCESS_STATUS, STUDENT_DEALINGS_STATUS_COLORS } from "../../constants";
import log from "../../../utility/logging";
import TutorCommentsPopup from "../TutorCommentsPopup";
import { adminEditDealState } from "../../../states/userRole";
import { useAtom } from "jotai";
import { StatusSwitch } from "../../StatusSwitch";
import "../tutor-status-rows.css";

type ShortlistedTutorData = {
  id: number;
  status: string;
  tutor_id: number;
  distance: number;
  gender: string;
  phone: string;
  tutor_name: string;
  dealings_id: number;
  tutor_status: string;
  shortlist_status: string;
  is_verified: boolean;
  expertise: string;
  is_applied: boolean;
  is_shortlisted: boolean;
  match_percentage: string;
  location: string;
  enquiry_date?: string;
  can_move_to_process?: boolean;
  comment_count: number;
  tutor_rating: number;
};

export function StudentRequirementsTutorsShortlistedTable(
  props: StudentRequirementsTutorsShortlistedTableProps
) {
  const { studentDetails, setStudentDealStatus } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [tutorsList, setTutorsList] = useState<Array<ShortlistedTutorData>>([]);
  const [metaInfo, setMetaInfo] = useState({ total: 0 });
  const [state, setState] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [canAdminEditDeal, _setCanAdminEditDeal] = useAtom(adminEditDealState);

  useEffect(() => {
    fetchTutorRecoList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, studentDetails.id, state]);

  async function fetchTutorRecoList() {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `/v1/cre/student/recommendation/shortlist`,
        {
          params: {
            dealings_id: studentDetails.id,
            page: 1,
            limit: 25,
          },
        }
      );
      console.log('setTutorsList', data.data?.data)
      setTutorsList(data.data?.data);
      setMetaInfo({ total: data.data?.meta?.total });
    } catch (error) {
      console.error(
        `Error fetching comments for record ${studentDetails.id}:`,
        error
      );
    } finally {
      setLoading(false);
    }
  }
  async function setShortlistedStatus(
    tutorId: string | number,
    recommendationId: string | number
  ) {
    try {
      const { data } = await axios.post(`v1/cre/student/shortlist-tutor`, {
        tutor_id: tutorId,
        dealings_id: studentDetails.id,
        recommendation_id: recommendationId,
      });
      if (data.success) {
        message.success("Successfully updated Shortlist status");
      } else {
        message.error(data.message ?? "Something went wrong");
      }
      setState(!state);
    } catch (error) {
      log.error(`Error fetching v1/cre/student/shortlist-tutor`, error);
    }
  }

  async function updateTutorStatus(
    field: "demo_date" | "status" | "confirmed_tuition_date" | "start_date",
    value: string | undefined,
    record: Record<string, unknown>
  ) {
    if (!value) return;
    try {
      setLoading(true);
      const { data } = await axios.post(
        `/v1/cre/student/process/update-status`,
        {
          dealings_id: studentDetails.id,
          shortlist_id: record.id,
          status: record.status,
          [field]: value,
        }
      );
      if (data.success) {
        setStudentDealStatus(data.data.dealStatus as keyof typeof STUDENT_DEALINGS_STATUS_COLORS);
        message.success("Successfully updated Process status");
      } else {
        message.error(data.message ?? "Somethign went wrong");
      }
      setState(!state);
    } catch (error) {
      console.log("Error updating tutor status:", error);
    } finally {
      setLoading(false);
    }
  }

  async function moveToProcess(
    tutorId: string | number,
    shortlistId: string | number
  ) {
    try {
      const { data } = await axios.post(`/v1/cre/student/assign-tutor`, {
        tutor_id: tutorId,
        dealings_id: studentDetails.id,
        // TODO shortlist id to be added
        shortlist_id: shortlistId,
      });
      if (data.success) {
        console.log(2);
        setStudentDealStatus(data.data.dealStatus as keyof typeof STUDENT_DEALINGS_STATUS_COLORS);
        console.log(3, data.data);
        fetchTutorRecoList();
      }
    } catch (error) {
      log.error(`Error moveToProcess ${studentDetails.id}:`, error);
    }
  }

  const _columns = [
    {
      title: "Enquiry Date",
      dataIndex: "enquiry_date",
      key: "enquiry_date",
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (
        cellValue: keyof typeof SHORTLISTED_TUTOR_PROCESS_STATUS,
        record: ShortlistedTutorData
      ) => (
        <StatusSwitch
          // disabled={!canAdminEditDeal}
          value={cellValue}
          options={SHORTLISTED_TUTOR_PROCESS_STATUS}
          onChange={(e) => {
            updateTutorStatus("status", e.target.value, record);
          }}
        />
      ),
      width: 150,
      align: "center",
    },
    {
      title: "Tutor Name",
      dataIndex: "tutor_name",
      key: "tutor_name",
      width: 180,
      align: "center",
      render: (_: any, record: Record<string, string>) => {
        return (
          <Tooltip placement="topLeft" title={record.tutor_name}>
            <div
              style={{
                padding: "5px",
              }}
            >
              {record.tutor_name}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      align: "center",
      width: 70,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 150,
    },
    {
      title: "Distance",
      dataIndex: "distance",
      key: "distance",
      align: "center",
      render: (cellValue: string) => <span>{cellValue} km</span>,
      width: 70,
    },
    {
      title: "Google Distance",
      dataIndex: "location",
      key: "location",
      render: renderDistanceLinkGen,
      width: 150,
    },
    {
      title: "Expertise",
      dataIndex: "expertise",
      key: "expertise",
      width: 250,
      ellipsis: true,
      render: (_: any, record: Record<string, string>) => {
        return (
          <Tooltip placement="topLeft" title={record.expertise}>
            <div
              style={{
                padding: "5px",
              }}
            >
              {record.expertise}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "MatchPercentage",
      dataIndex: "match_percentage",
      key: "match_percentage",
      width: 100,
      render: (cellValue: string) => <span>{cellValue} %</span>,
    },
    {
      title: "Rating",
      dataIndex: "tutor_rating",
      key: "tutor_rating",
      width: 70,
      align: "center",
      render: (cellValue: number) =>
        cellValue > 0 ? (
          <span style={{ color: "#faad14" }}>
            {"★".repeat(Math.round(cellValue))}
            <span style={{ color: "#d9d9d9" }}>
              {"★".repeat(5 - Math.round(cellValue))}
            </span>
          </span>
        ) : (
          <span style={{ color: "#d9d9d9" }}>★★★★★</span>
        ),
    },
    {
      title: "Shortlisted",
      dataIndex: "is_shortlisted",
      key: "is_shortlisted",
      align: "center",
      fixed: true,
      render: (cellValue: boolean, rowRecordObject: Record<string, string>) => (
        <Flex align="center" justify="center">
          <Button
            disabled={!canAdminEditDeal}
            className="mx-auto"
            type="link"
            shape="circle"
            iconPosition="end"
            icon={cellValue ? <HeartFilled /> : <HeartOutlined />}
            onClick={() => {
              setShortlistedStatus(
                rowRecordObject.tutor_id,
                rowRecordObject.id
              );
            }}
          ></Button>
        </Flex>
      ),
      width: 100,
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 200,
      align: "center",
      render: (cellValue: string, record: ShortlistedTutorData) => (
        <TutorCommentsPopup
          cellValue={cellValue}
          data={{
            shortlist_id: record.id,
            comment_count: record.comment_count,
          }}
        />
      ),
    },
    {
      title: "Verified",
      dataIndex: "is_verified",
      key: "is_verified",
      render: renderBoolean,
      align: "center",
      width: 100,
      onCell: (record: ShortlistedTutorData) => ({
        style: {
          color: record.is_verified ? "#ffffff" : "#000000",
          backgroundColor: record.is_verified ? "#1bb83d" : "#ffffff",
        },
      }),
    },
    {
      title: "Applied",
      dataIndex: "is_applied",
      key: "is_applied",
      render: renderBoolean,
      align: "center",
      width: 100,
      onCell: (record: ShortlistedTutorData) => ({
        style: {
          color: record.is_applied ? "#ffffff" : "#000000",
          backgroundColor: record.is_applied ? "#ddb42d" : "#ffffff",
        },
      }),
    },
    {
      title: "Move to Process",
      dataIndex: "move_to_process",
      key: "move_to_process",
      render: (cellValue: boolean, rowRecordObject: ShortlistedTutorData) => (
        <Button
          // disabled={!rowRecordObject.can_move_to_process || !canAdminEditDeal}
          disabled={!rowRecordObject.can_move_to_process}
          onClick={() =>
            moveToProcess(rowRecordObject.tutor_id, rowRecordObject.id)
          }
          className="tail-px-2 tail-mx-auto"
          icon={
            <ArrowRightOutlined className="tail-border-2 tail-text-blue-400 tail-border-current tail-p-0.5 tail-rounded-md" />
          }
          iconPosition="end"
          type="text"
          variant="outlined"
        >
          Move
        </Button>
      ),
      align: "center",
      width: 200,
    },
    {
      title: "Tutor Details",
      dataIndex: "tutor_id",
      key: "tutor_id",
      align: "center",
      width: 100,
      render: (cellValue: string) => (
        <Button
          className="tail-mx-auto"
          type="text"
          iconPosition="end"
          icon={<InfoCircleFilled className="tail-text-blue-500" />}
          href={`/tutor/${cellValue}`}
          target="_blank"
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <Table
      dataSource={tutorsList as TableProps<ShortlistedTutorData>["dataSource"]}
      loading={loading}
      pagination={{
        total: metaInfo.total,
        pageSize: 25,
        onChange: (page, pageSize) => setPage(page),
        showSizeChanger: false,
        current: page,
      }}
      bordered={true}
      onChange={(pagination, filters, sorter, extra) => {
        console.log(
          "pagination",
          pagination,
          "filters",
          filters,
          "sorter",
          sorter,
          "extra",
          extra
        );
      }}
      tableLayout="fixed"
      scroll={{ x: "100%" }}
      rowClassName={(record) => {
        const status = (record.tutor_status || "").toLowerCase();
        if (status === "blocked" || status === "block") return "tutor-row-blocked";
        if (status === "inactive") return "tutor-row-inactive";
        if (status === "warning") return "tutor-row-warning";
        return "";
      }}
      columns={_columns as TableProps<ShortlistedTutorData>["columns"]}
    />
  );
}
